import React from 'react';
import {ChevronDown, ChevronRight} from 'lucide-react';
import {Collapsible, CollapsibleContent, CollapsibleTrigger} from "../ui/collapsible";
import {MarkdownRenderer} from "./MarkdownRenderer";

// Import specific tool renderers
import { TimeSeriesToolRenderer } from './toolRenderers/TimeSeriesToolRenderer';
import { LogsToolRenderer } from './toolRenderers/LogsToolRenderer';
import { TracesToolRenderer } from './toolRenderers/TracesToolRenderer';
import { ServiceSummariesToolRenderer } from './toolRenderers/ServiceSummariesToolRenderer';
import { EnvironmentsToolRenderer } from './toolRenderers/EnvironmentsToolRenderer';
import { MetricNamesToolRenderer } from './toolRenderers/MetricNamesToolRenderer';
import { AttributeKeysToolRenderer } from './toolRenderers/AttributeKeysToolRenderer';
import { AttributeValuesToolRenderer } from './toolRenderers/AttributeValuesToolRenderer';
import { ServicesToolRenderer } from './toolRenderers/ServicesToolRenderer';
import { NamespacesToolRenderer } from './toolRenderers/NamespacesToolRenderer';
import { MetricMetadataToolRenderer } from './toolRenderers/MetricMetadataToolRenderer';
import { NodesToolRenderer } from './toolRenderers/NodesToolRenderer';
import { AlertsToolRenderer } from './toolRenderers/AlertsToolRenderer';
import { SourceRepositoryToolRenderer } from './toolRenderers/SourceRepositoryToolRenderer';
import { SearchCodeToolRenderer } from './toolRenderers/SearchCodeToolRenderer';
import { FileContentsToolRenderer } from './toolRenderers/FileContentsToolRenderer';
import {TraceFlameGraphToolRenderer} from "./toolRenderers/TraceFlameGraphToolRenderer";
import { CreateIssueToolRenderer } from './toolRenderers/CreateIssueToolRenderer';
import { ListIssuesToolRenderer } from './toolRenderers/ListIssuesToolRenderer';
import { K8sEventsToolRenderer } from './toolRenderers/K8sEventsToolRenderer';
import { ServiceYamlToolRenderer } from './toolRenderers/ServiceYamlToolRenderer';
import { ProfilesToolRenderer } from './toolRenderers/ProfilesToolRenderer';
import { ServiceGraphToolRenderer } from './toolRenderers/ServiceGraphToolRenderer';
import { SubAgentToolRenderer } from './toolRenderers/SubAgentToolRenderer';

// Message interface matching the one from GuardianChat
interface Message {
    role: 'user' | 'assistant' | 'system' | 'tool';
    content: string;
    tool_name?: string;
    tool_call?: boolean;
    tool_call_id?: string;
    tool_call_function_name?: string;
    tool_call_function_args?: string;
    tool_call_response_id?: string;
    tool_call_response_content?: string;
    screenshot?: string;
}

// For rendering custom tool responses
export interface ToolRendererProps {
    message: Message;
    isToolCall: boolean;
}

// Static function interface for checking if a tool message should be rendered
export interface ToolRendererStatic {
    // Returns true if the message should be rendered, false to skip rendering completely
    shouldRender?(message: Message): boolean;
}

// Type for a tool renderer component that may also have static methods
export type ToolRendererComponent = React.FC<ToolRendererProps> & ToolRendererStatic;

// Default tool call and response renderers
export function DefaultToolCallRenderer({message}: { message: Message }) {
    const [isParamsOpen, setIsParamsOpen] = React.useState(false);

    return (
        <div className="space-y-2">
            <div className="items-center gap-2 text-sm text-textmedium">
                <MarkdownRenderer content={message.content}/>
                <div className="flex items-center gap-2 text-textmedium">
                    <span>{message.tool_name}</span>
                </div>
            </div>
            {message.tool_call_function_args && (
                <Collapsible open={isParamsOpen} onOpenChange={setIsParamsOpen}>
                    <CollapsibleTrigger
                        className="flex items-center gap-2 text-sm text-textmedium hover:text-textlight transition-colors">
                        {isParamsOpen ? <ChevronDown className="h-4 w-4"/> : <ChevronRight className="h-4 w-4"/>}
                        <span>Tool Parameters</span>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                        <div
                            className="mt-2 bg-[#1D2739] rounded-lg p-4 text-sm font-mono text-textmedium overflow-x-auto">
                            <pre className="whitespace-pre-wrap">
                                {JSON.stringify(JSON.parse(message.tool_call_function_args), null, 2)}
                            </pre>
                        </div>
                    </CollapsibleContent>
                </Collapsible>
            )}
        </div>
    );
}

export function DefaultToolResponseRenderer({message}: { message: Message }) {
    const [isOpen, setIsOpen] = React.useState(false);

    return (
        <Collapsible open={isOpen} onOpenChange={setIsOpen} className="w-full">
            <CollapsibleTrigger
                className="flex items-center gap-2 text-sm text-textmedium hover:text-textlight transition-colors">
                {isOpen ? <ChevronDown className="h-4 w-4"/> : <ChevronRight className="h-4 w-4"/>}
                <span>Tool Response</span>
            </CollapsibleTrigger>
            <CollapsibleContent>
                <div className="mt-2 bg-[#1D2739] rounded-lg p-4 text-sm font-mono text-textmedium overflow-x-auto">
                    <pre className="whitespace-pre-wrap">{message.tool_call_response_content || message.content}</pre>
                </div>
            </CollapsibleContent>
        </Collapsible>
    );
}

// Registry of tool renderers by tool name
export const toolRenderers: Record<string, ToolRendererComponent> = {
    // Map tool function names to renderer components
    "get_timeseries_data": TimeSeriesToolRenderer,
    "get_logs": LogsToolRenderer,
    "get_traces": TracesToolRenderer,
    "get_service_summaries": ServiceSummariesToolRenderer,
    "get_environments": EnvironmentsToolRenderer,
    "get_metric_names": MetricNamesToolRenderer,
    "get_attribute_keys": AttributeKeysToolRenderer,
    "get_attribute_values": AttributeValuesToolRenderer,
    "get_services": ServicesToolRenderer,
    "get_namespaces": NamespacesToolRenderer,
    "get_metric_metadata": MetricMetadataToolRenderer,
    "get_nodes": NodesToolRenderer,
    "get_alerts": AlertsToolRenderer,
    "get_source_repository": SourceRepositoryToolRenderer,
    "search_code": SearchCodeToolRenderer,
    "get_file_contents": FileContentsToolRenderer,
    "get_trace_spans": TraceFlameGraphToolRenderer,
    "create_issue": CreateIssueToolRenderer,
    "list_issues": ListIssuesToolRenderer,
    "get_k8s_events": K8sEventsToolRenderer,
    "get_service_yaml": ServiceYamlToolRenderer,
    "get_profiles": ProfilesToolRenderer,
    "get_k8s_events_attributes": AttributeKeysToolRenderer,
    "get_k8s_event_attribute_values_for_individual_attribute": AttributeValuesToolRenderer,
    "get_service_graph": ServiceGraphToolRenderer,
    
    // Sub Agent Tools
    "log_querier": SubAgentToolRenderer,
    "metric_querier": SubAgentToolRenderer,
    "trace_querier": SubAgentToolRenderer,
    "coder": SubAgentToolRenderer,
    "profile_querier": SubAgentToolRenderer,
    "kubernetes_resource_querier": SubAgentToolRenderer,
    
    // Handle generic agent calls with the SubAgentToolRenderer
    "guardian": SubAgentToolRenderer,
};